/*
 * ReminderDetailsModal — read-only details of one reminder plus a notes thread.
 *
 * Opened from the "view" icon in the dashboard's Actions column. Editing is
 * still done via the existing edit modal — this view is intentionally focused
 * on inspecting and discussing a task rather than mutating its core fields.
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import {
  FaRegStickyNote,
  FaClock,
  FaUserCircle,
  FaLink,
  FaTag,
  FaBuilding,
  FaRegBell,
  FaTimes,
  FaCheckCircle,
  FaRegCircle,
  FaGlobe,
  FaHistory,
  FaDownload,
  FaShareAlt,
  FaCheck
} from 'react-icons/fa';

import Linkify from 'linkify-react';
import CustomModal from '../../util_components/custom_modal';
import PriorityIcon from './PriorityIcon';
import ReminderNotes from './ReminderNotes';
import { formatDue, formatRelativeDue, isPastDue } from './constants';
import {
  team_member_by_uuid,
  task_assignee_uuids,
  linked_user_by_uuid,
  label_by_uuid,
  department_by_uuid,
  REMINDER_OFFSETS
} from './api';
import './reminders.css';

function SectionHeading({ icon: Icon, children }) {
  return (
    <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-content-muted">
      {Icon ? <Icon className="text-content-muted" size={11} /> : null}
      {children}
    </div>
  );
}

// Pretty-print an alert offset using the same labels as the form dropdown.
const alertLabel = (minutes) => {
  const found = REMINDER_OFFSETS.find((o) => o.minutes === minutes);
  return found ? found.label : `${minutes} min before`;
};

// Resolve a uuid to a human name via the team-members cache; falls back to
// the raw uuid (truncated) when the cache doesn't know the user.
const nameForUuid = (uuid) => {
  const tm = team_member_by_uuid(uuid);
  return tm?.name || uuid?.slice(0, 8) || 'someone';
};

// Format an ISO-ish string the same way the notes thread does — relative
// fromNow in the visible area, absolute in the title= tooltip.
const formatRelativeAt = (at) => moment(at).fromNow();
const formatAbsoluteAt = (at) => moment(at).format('llll');

// Render a "due_at" string for the rescheduled diff. Both `at_time` columns
// are full ISO timestamps; reuse formatDue with has_time forced on so the
// time component is always visible (the reschedule may have happened on a
// has_time=false task but the operator still wants the exact value).
const formatDueAt = (iso) => formatDue({ due_at: iso, has_time: true });

// Diff two string-array snapshots into [added, removed] lists.
const diffArrays = (from = [], to = []) => {
  const fromSet = new Set(from);
  const toSet = new Set(to);
  const added = to.filter((x) => !fromSet.has(x));
  const removed = from.filter((x) => !toSet.has(x));
  return { added, removed };
};

const HISTORY_VERB = {
  created: 'Created the reminder',
  updated: 'Edited the reminder',
  rescheduled: 'Rescheduled',
  reassigned: 'Reassigned',
  completed: 'Marked completed',
  reopened: 'Reopened',
  note_added: 'Added a note',
  note_deleted: 'Deleted a note',
  description_changed: 'Edited description',
  alerts_changed: 'Changed reminders'
};

// Renders one history row. The header line is "VERB · by NAME · timeAgo";
// rich diff types (rescheduled / reassigned / description / alerts) get an
// extra small block below with the before/after detail.
function HistoryRow({ entry }) {
  const verb = HISTORY_VERB[entry.type] || `Updated (${entry.type})`;
  const who = entry.by || nameForUuid(entry.by_uuid);

  let detail = null;
  if (entry.type === 'rescheduled' && entry.from_due_at && entry.to_due_at) {
    detail = (
      <div className="mt-0.5 text-[12px] text-content-secondary">
        <span className="text-content-muted">{formatDueAt(entry.from_due_at)}</span>
        <span className="mx-1 text-content-muted">→</span>
        <span className="text-content-secondary">{formatDueAt(entry.to_due_at)}</span>
      </div>
    );
  } else if (entry.type === 'reassigned') {
    const { added, removed } = diffArrays(entry.from || [], entry.to || []);
    detail = (
      <div className="mt-0.5 space-y-0.5 text-[12px]">
        {added.map((u) => (
          <div key={`+${u}`} className="text-state-success">
            + {nameForUuid(u)}
          </div>
        ))}
        {removed.map((u) => (
          <div key={`-${u}`} className="text-state-error">
            − {nameForUuid(u)}
          </div>
        ))}
      </div>
    );
  } else if (entry.type === 'description_changed') {
    const before = entry.from?.[0] || '';
    const after = entry.to?.[0] || '';
    detail = (
      <div className="mt-1 grid grid-cols-2 gap-2 text-[12px]">
        <div className="rounded border border-line bg-surface-sunken px-2 py-1">
          <div className="mb-0.5 text-[10px] uppercase tracking-wider text-content-muted">
            Before
          </div>
          <div className="line-clamp-3 whitespace-pre-wrap break-words text-content-secondary">
            {before || <span className="italic text-content-muted">empty</span>}
          </div>
        </div>
        <div className="rounded border border-line bg-surface-card px-2 py-1">
          <div className="mb-0.5 text-[10px] uppercase tracking-wider text-content-muted">
            After
          </div>
          <div className="line-clamp-3 whitespace-pre-wrap break-words text-content-primary">
            {after || <span className="italic text-content-muted">empty</span>}
          </div>
        </div>
      </div>
    );
  } else if (entry.type === 'alerts_changed') {
    const before = (entry.from || []).map((s) => alertLabel(parseInt(s, 10)));
    const after = (entry.to || []).map((s) => alertLabel(parseInt(s, 10)));
    detail = (
      <div className="mt-0.5 text-[12px] text-content-secondary">
        <span className="text-content-muted">{before.length ? before.join(', ') : 'none'}</span>
        <span className="mx-1 text-content-muted">→</span>
        <span className="text-content-secondary">{after.length ? after.join(', ') : 'none'}</span>
      </div>
    );
  }

  return (
    <li className="rounded-md border border-line bg-surface-card px-3 py-2 text-sm text-content-primary shadow-sm">
      <div className="flex items-center justify-between gap-2 text-[12px]">
        <span className="font-medium text-content-secondary">{verb}</span>
        <span className="text-content-muted" title={formatAbsoluteAt(entry.at)}>
          {formatRelativeAt(entry.at)}
        </span>
      </div>
      <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-content-muted">
        <FaUserCircle size={10} className="text-content-muted" /> {who}
      </div>
      {detail}
    </li>
  );
}

HistoryRow.propTypes = {
  entry: PropTypes.shape({
    type: PropTypes.string,
    at: PropTypes.string,
    by: PropTypes.string,
    by_uuid: PropTypes.string,
    from_due_at: PropTypes.string,
    to_due_at: PropTypes.string,
    from: PropTypes.arrayOf(PropTypes.string),
    to: PropTypes.arrayOf(PropTypes.string)
  }).isRequired
};

// Copy text to the clipboard, falling back to a hidden textarea +
// execCommand for browsers/contexts where navigator.clipboard is unavailable.
const copyToClipboard = (text) => {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve, reject) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      resolve();
    } catch (err) {
      reject(err);
    }
  });
};

function ReminderDetailsModal({ show, close, task, onUpdated }) {
  // Transient "link copied" confirmation on the header share button.
  const [copied, setCopied] = useState(false);

  // Reset transient state whenever we open or switch tasks.
  useEffect(() => {
    if (show) {
      setCopied(false);
    }
  }, [show, task?.uuid]);

  if (!task) return null;

  // Copy a permalink to this task. Opening it (see Reminders index) re-opens
  // this details modal via the ?task=<uuid> query param.
  const handleShare = () => {
    const { origin, pathname } = window.location;
    const url = `${origin}/reminders?task=${encodeURIComponent(task.uuid)}`;
    copyToClipboard(url)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('reminders: copy permalink failed', err);
      });
  };

  const assignees = task_assignee_uuids(task).map(team_member_by_uuid).filter(Boolean);
  const linked = linked_user_by_uuid(task.linked_user_uuid);
  const labels = (task.label_uuids || []).map(label_by_uuid).filter(Boolean);
  const department = department_by_uuid(task.department_uuid);
  const overdue = isPastDue(task);
  const attachments = Array.isArray(task.attachments) ? task.attachments : [];

  const attachmentName = (attachment) =>
    attachment.name || attachment.key?.split('/').pop() || 'attachment';

  return (
    <CustomModal
      show={show}
      close={close}
      showCloseBtn
      customClose
      inner_style={{
        width: 'min(1120px, 96vw)',
        maxWidth: 'none',
        maxHeight: '90vh',
        padding: '0',
        overflow: 'hidden',
        borderRadius: '14px'
      }}
    >
      <div className="reminders-scope flex max-h-[85vh] flex-col bg-surface-card">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-3">
          <div className="flex min-w-0 items-start gap-2">
            <PriorityIcon priority={task.priority} size={28} />
            <div className="min-w-0">
              <div
                className={`break-words text-base font-semibold ${
                  task.status === 'completed'
                    ? 'text-content-muted line-through'
                    : 'text-content-primary'
                }`}
              >
                {task.title}
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] text-content-muted">
                <span
                  className={`inline-flex items-center gap-1 ${overdue ? 'text-state-error font-medium' : ''}`}
                >
                  <FaClock size={10} />
                  {formatRelativeDue(task)}
                  <span className="text-content-muted"> · {formatDue(task)}</span>
                </span>
                <span className="inline-flex items-center gap-1">
                  {task.status === 'completed' ? (
                    <FaCheckCircle className="text-state-success" size={10} />
                  ) : (
                    <FaRegCircle size={10} />
                  )}
                  {task.status === 'completed' ? 'Completed' : 'Pending'}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={handleShare}
              aria-label="Copy link to this reminder"
              title={copied ? 'Link copied' : 'Copy link to this reminder'}
              className="reminders-close-btn inline-flex h-8 w-8 shrink-0 items-center justify-center self-center"
            >
              {copied ? (
                <FaCheck size={12} className="text-state-success" />
              ) : (
                <FaShareAlt size={12} />
              )}
            </button>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            title="Close"
            className="reminders-close-btn inline-flex h-8 w-8 shrink-0 items-center justify-center"
          >
            <FaTimes size={12} />
          </button>
        </div>

        {/* Body */}
        <div className="grid min-h-0 flex-1 grid-cols-12 gap-0 overflow-hidden">
          {/*
            Left — details. `min-w-0` is critical here: CSS grid items
            default to `min-width: auto`, which lets the cell's intrinsic
            content (e.g. a 1920px-wide image inside the description) push
            the cell wider than its column allocation, spilling into the
            notes pane. Forcing the min-width to 0 makes the cell hold its
            6/12 column width and `max-width: 100%` on <img>/<video> kicks in
            properly. The right pane gets the same fix below for symmetry.
          */}
          <div className="col-span-12 min-w-0 overflow-y-auto border-r border-line px-5 py-4 md:col-span-6">
            {/* Description + attachments */}
            <div className="mb-6">
              <SectionHeading icon={FaRegStickyNote}>Description</SectionHeading>
              {task.description ? (
                <div className="whitespace-pre-wrap break-words text-sm text-content-secondary">
                  <Linkify
                    options={{
                      target: '_blank',
                      rel: 'noopener noreferrer',
                      className: 'text-state-info hover:underline'
                    }}
                  >
                    {task.description}
                  </Linkify>
                </div>
              ) : (
                <div className="text-sm text-content-muted">No description.</div>
              )}
              {attachments.length > 0 ? (
                <div className="mt-3 flex w-full max-w-full flex-col gap-2 overflow-hidden">
                  {attachments.map((a) => {
                    const isImage = (a.type || '').startsWith('image/');
                    const isVideo = (a.type || '').startsWith('video/');
                    const name = attachmentName(a);
                    const url = a.url || '#';

                    if (isImage && a.url) {
                      return (
                        <a
                          key={a.key}
                          href={url}
                          download={name}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="reminders-attachment-media-link reminders-attachment-image-link group"
                          title={name}
                          aria-label={`Download ${name}`}
                        >
                          <img
                            src={a.url}
                            alt={name}
                            className="reminders-attachment-media max-h-80 object-contain"
                          />
                          <span className="reminders-attachment-download-overlay">
                            <FaDownload size={18} />
                          </span>
                        </a>
                      );
                    }
                    if (isVideo && a.url) {
                      return (
                        <a
                          key={a.key}
                          href={url}
                          download={name}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="reminders-attachment-media-link group bg-black"
                          title={name}
                          aria-label={`Download ${name}`}
                        >
                          <video
                            src={a.url}
                            className="reminders-attachment-media max-h-80 object-contain"
                            muted
                            controls
                          />
                        </a>
                      );
                    }

                    return (
                      <a
                        key={a.key}
                        href={url}
                        download={name}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="reminders-attachment-file-row"
                        title={name}
                        aria-label={`Download ${name}`}
                      >
                        <span className="min-w-0 flex-1 truncate">{name}</span>
                        <FaDownload size={11} className="shrink-0 text-content-muted" />
                      </a>
                    );
                  })}
                </div>
              ) : null}
            </div>

            {/* Assignees */}
            <div className="mb-6">
              <SectionHeading icon={FaUserCircle}>Assignees</SectionHeading>
              {assignees.length === 0 ? (
                <div className="text-sm text-content-muted">Unassigned</div>
              ) : (
                <div className="flex flex-col gap-1">
                  {assignees.map((a) => (
                    <div
                      key={a.uuid}
                      className="flex items-center gap-1.5 text-sm text-content-secondary"
                    >
                      <FaUserCircle size={12} className="text-content-muted" />
                      <span>{a.name}</span>
                      {a.email ? (
                        <span className="text-[11px] text-content-muted">· {a.email}</span>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Linked user */}
            <div className="mb-6">
              <SectionHeading icon={FaLink}>User</SectionHeading>
              {linked ? (
                <div className="flex items-center gap-1.5 text-sm text-content-secondary">
                  <FaLink size={10} className="text-content-muted" />
                  <span className="text-content-muted">
                    {linked.role === 'teacher' ? 'Coach' : 'Student'}:
                  </span>
                  {linked.uuid ? (
                    <a
                      href={`${linked.role === 'teacher' ? '/teacherprofile' : '/studentprofile'}?id=${encodeURIComponent(linked.uuid)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-primary hover:underline"
                    >
                      {linked.name}
                    </a>
                  ) : (
                    <span>{linked.name}</span>
                  )}
                  {linked.iana_timezone ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-content-muted">
                      <FaGlobe size={9} /> {linked.iana_timezone}
                    </span>
                  ) : null}
                </div>
              ) : (
                <div className="text-sm text-content-muted">Not linked</div>
              )}
            </div>

            {/* Labels */}
            <div className="mb-6">
              <SectionHeading icon={FaTag}>Labels</SectionHeading>
              {labels.length === 0 ? (
                <div className="text-sm text-content-muted">No labels</div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {labels.map((l) => (
                    <span
                      key={l.uuid}
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] text-content-secondary"
                      style={{ background: l.color }}
                    >
                      <FaTag size={8} className="opacity-50" />
                      {l.name}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Department (single-select). Omitted when the reminder has
                no department — saves an "No department" line of noise. */}
            {department ? (
              <div className="mb-6">
                <SectionHeading icon={FaBuilding}>Department</SectionHeading>
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] text-content-secondary"
                  style={{ background: department.color }}
                >
                  <FaBuilding size={8} className="opacity-50" />
                  {department.name}
                </span>
              </div>
            ) : null}

            {/* Alerts */}
            <div className="mb-6">
              <SectionHeading icon={FaRegBell}>Reminders</SectionHeading>
              {Array.isArray(task.alerts) && task.alerts.length > 0 ? (
                <ul className="list-disc pl-4 text-sm text-content-secondary">
                  {task.alerts.map((a, i) => (
                    <li key={`${a.offset_minutes}-${i}`}>{alertLabel(a.offset_minutes)}</li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-content-muted">None</div>
              )}
            </div>

            {/* History — newest first. Lives inside the same scrollable
                left pane (overflow-y-auto on the wrapper) so the timeline
                can grow without breaking the modal layout. */}
            <div className="mb-2">
              <SectionHeading icon={FaHistory}>History</SectionHeading>
              {Array.isArray(task.history) && task.history.length > 0 ? (
                <ul className="space-y-2">
                  {[...task.history].reverse().map((h, i) => (
                    // history rows have no stable per-event id, so index is
                    // the only key option; the array is rebuilt from the
                    // server response on every refresh so reuse is fine.
                    // eslint-disable-next-line react/no-array-index-key
                    <HistoryRow key={i} entry={h} />
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-content-muted">No changes yet.</div>
              )}
            </div>
          </div>

          {/* Right — notes */}
          <div className="col-span-12 flex min-h-0 min-w-0 flex-col bg-surface-sunken px-5 py-4 md:col-span-6">
            <ReminderNotes
              taskUuid={task.uuid}
              notes={task.notes}
              onUpdated={onUpdated}
              tooltipId="reminder-details-tooltip"
            />
          </div>
        </div>
      </div>
      {/*
        Local tooltip instance. The app-root tooltip in App.jsx uses
        z-index 100 which sits below the CustomModal overlay, so hovers
        inside this modal would render the tooltip behind the modal.
        Mounting our own instance here lets us bump z-index above the
        modal while keeping the simple data-attribute API.
      */}
      <ReactTooltip
        id="reminder-details-tooltip"
        place="top"
        style={{ zIndex: 100000, fontSize: 11, padding: '4px 8px' }}
      />
    </CustomModal>
  );
}

export default ReminderDetailsModal;
